import React, { useState } from 'react';
import { LOGO_HORIZONTAL } from '../data/images';
import { CONTACT, MEMBERSHIP } from '../data/config';

// ── CONTACT SECTION ───────────────────────────────────────────
// Contact info is pulled from src/data/config.js → CONTACT
//
// The message box was removed since there's no backend processing it yet —
// submitting just opens the visitor's email client via mailto, so they can
// write their actual message there. Re-add the textarea (see git history /
// prior version) once a real submission handler (EmailJS, Formspree, etc.)
// is wired up.

export function ContactSection() {
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', subject: '' });
  const [sent, setSent] = useState(false);

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = e => {
    e.preventDefault();
    // In production: connect to EmailJS, Formspree, or a backend API
    // UPDATE: replace with real form submission logic
    const mailto = `mailto:${CONTACT.email}?subject=${encodeURIComponent(form.subject || 'Website Inquiry')}&body=${encodeURIComponent(`From: ${form.firstName} ${form.lastName} (${form.email})`)}`;
    window.location.href = mailto;
    setSent(true);
    setTimeout(() => setSent(false), 4000);
  };

  return (
    <section id="contact" style={{ padding: '80px 24px', background: 'white' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr',
          gap: 64, alignItems: 'start',
        }} className="contact-grid">

          {/* Left: Info */}
          <div>
            <span className="section-label">Get in Touch</span>
            <h2 style={{
              fontFamily: 'Playfair Display, serif',
              fontSize: 'clamp(2rem, 3.5vw, 2.8rem)',
              fontWeight: 900, lineHeight: 1.2, marginBottom: 20, color: '#1A1A1A',
            }}>
              We'd Love to Hear From You
            </h2>
            <p style={{ color: '#666', fontSize: 15, lineHeight: 1.8, marginBottom: 36 }}>
              Whether you're a prospective member, a company interested in sponsoring, or have any questions — reach out. We'll get back to you quickly.
            </p>

            {/* Contact info cards */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {[
                { icon: '✉️', label: 'Email', value: CONTACT.email, href: `mailto:${CONTACT.email}` },
                { icon: '📍', label: 'Address', value: CONTACT.address, href: null },
                { icon: '📸', label: 'Instagram', value: '@shpe_vt', href: CONTACT.instagram },
                { icon: 'in', label: 'LinkedIn', value: 'SHPE@VT on LinkedIn', href: CONTACT.linkedin },
              ].map((item, i) => (
                <a key={i} href={item.href || undefined}
                  target={item.href && !item.href.startsWith('mailto') ? '_blank' : undefined}
                  rel="noopener noreferrer"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 16,
                    padding: '16px 20px', borderRadius: 12,
                    border: '1px solid #EEE', textDecoration: 'none', color: 'inherit',
                    transition: 'all 0.2s ease',
                    cursor: item.href ? 'pointer' : 'default',
                  }}
                  onMouseEnter={e => item.href && (e.currentTarget.style.borderColor = '#E5751F')}
                  onMouseLeave={e => item.href && (e.currentTarget.style.borderColor = '#EEE')}>
                  <div style={{
                    width: 44, height: 44, borderRadius: 10, flexShrink: 0,
                    background: '#F5E8EB', display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                    fontSize: item.icon === 'in' ? 16 : 20, fontWeight: 900,
                    color: item.icon === 'in' ? '#0A66C2' : undefined,
                  }}>
                    {item.icon}
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: '#AAA', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>{item.label}</div>
                    <div style={{ fontSize: 14, color: '#1A1A1A', marginTop: 2, fontWeight: 500 }}>{item.value}</div>
                  </div>
                </a>
              ))}
            </div>
          </div>

          {/* Right: Form */}
          <div style={{ background: '#F9F5F6', borderRadius: 20, padding: 36 }}>
            {sent ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <div style={{ fontSize: 56, marginBottom: 16 }}>📨</div>
                <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: 22, fontWeight: 700, color: '#1A1A1A', marginBottom: 8 }}>
                  Message Sent!
                </h3>
                <p style={{ color: '#666' }}>Your email client should have opened. We'll get back to you soon!</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: 20, fontWeight: 700, marginBottom: 24 }}>
                  Send a Message
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                  <FormField label="First Name" name="firstName" value={form.firstName} onChange={handleChange} required />
                  <FormField label="Last Name"  name="lastName"  value={form.lastName}  onChange={handleChange} required />
                </div>
                <FormField label="Email"   name="email"   value={form.email}   onChange={handleChange} type="email" required mb />
                <FormField label="Subject" name="subject" value={form.subject} onChange={handleChange} mb />
                <p style={{ fontSize: 13, color: '#888', marginBottom: 20, lineHeight: 1.6 }}>
                  Clicking below opens your email app with these details filled in — add your message there before sending.
                </p>
                <button type="submit" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', fontSize: 15, padding: '14px' }}>
                  Send Message →
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .contact-grid { grid-template-columns: 1fr !important; gap: 40px !important; }
        }
      `}</style>
    </section>
  );
}

function FormField({ label, name, value, onChange, type = 'text', required, mb }) {
  return (
    <div style={{ marginBottom: mb ? 12 : 0 }}>
      <label style={{ fontSize: 12, fontWeight: 700, color: '#888', letterSpacing: 0.5, display: 'block', marginBottom: 6 }}>
        {label.toUpperCase()}
      </label>
      <input
        type={type} name={name} value={value} onChange={onChange} required={required}
        style={{
          width: '100%', padding: '12px 16px', borderRadius: 10,
          border: '1px solid #E0D0D4', fontSize: 15, fontFamily: 'inherit',
          outline: 'none', boxSizing: 'border-box',
        }}
      />
    </div>
  );
}

// ── FOOTER ────────────────────────────────────────────────────
export function Footer() {
  const year = new Date().getFullYear();

  const footerLinks = {
    'Organization': [
      { label: 'About Us', href: '#about' },
      { label: 'E-Board', href: '#eboard' },
      { label: 'Programs', href: '#programs' },
      { label: 'Sponsors', href: '#sponsors' },
    ],
    'Engagement': [
      { label: 'Events Calendar', href: '#calendar' },
      { label: 'Newsletter', href: '#newsletter' },
      { label: 'Donate', href: '#donate' },
    ],
    'Connect': [
      { label: 'Contact Us', href: '#contact' },
      { label: 'Instagram', href: CONTACT.instagram, external: true },
      { label: 'LinkedIn', href: CONTACT.linkedin, external: true },
      { label: 'Twitter/X', href: CONTACT.twitter, external: true },
    ],
  };

  return (
    <footer style={{ background: '#0D0D0D', color: 'white', padding: '64px 24px 24px' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>

        <div style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr 1fr 1fr',
          gap: 48, marginBottom: 48,
        }} className="footer-grid">

          {/* Brand column */}
          <div>
            <img src={LOGO_HORIZONTAL} alt="SHPE@VT" style={{ height: 36, marginBottom: 16, filter: 'brightness(1.1)' }}
              onError={e => { e.target.style.display = 'none'; }} />
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 14, lineHeight: 1.8, marginBottom: 20, maxWidth: 280 }}>
              Society of Hispanic Professional Engineers at Virginia Tech. Empowering the next generation of Hispanic engineers.
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              {[
                { icon: '📷', href: CONTACT.instagram, label: 'Instagram' },
                { icon: '🐦', href: CONTACT.twitter,   label: 'Twitter' },
                { icon: '💼', href: CONTACT.linkedin,  label: 'LinkedIn' },
              ].map((s, i) => (
                <a key={i} href={s.href} target="_blank" rel="noopener noreferrer"
                  aria-label={s.label}
                  style={{
                    width: 38, height: 38, borderRadius: 10,
                    background: 'rgba(255,255,255,0.08)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 16, transition: 'background 0.2s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(229,117,31,0.25)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}>
                  {s.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Link columns */}
          {Object.entries(footerLinks).map(([group, links]) => (
            <div key={group}>
              <h4 style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: '#E5751F', marginBottom: 16 }}>
                {group}
              </h4>
              <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
                {links.map(link => (
                  <li key={link.label}>
                    <a href={link.href}
                      target={link.external ? '_blank' : undefined}
                      rel="noopener noreferrer"
                      style={{
                        color: 'rgba(255,255,255,0.55)', fontSize: 14,
                        transition: 'color 0.2s',
                        textDecoration: 'none',
                      }}
                      onMouseEnter={e => e.currentTarget.style.color = 'white'}
                      onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.55)'}>
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom bar */}
        <div style={{
          borderTop: '1px solid rgba(255,255,255,0.08)',
          paddingTop: 24,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          flexWrap: 'wrap', gap: 12,
        }}>
          <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: 13 }}>
            © {year} SHPE@VT — Virginia Tech Chapter · {CONTACT.address}
          </p>
          <p style={{ color: 'rgba(255,255,255,0.2)', fontSize: 12 }}>
            {/* UPDATE: change this tagline or remove */}
            Ut Prosim — That I May Serve
          </p>
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .footer-grid { grid-template-columns: 1fr 1fr !important; gap: 32px !important; }
          .footer-grid > div:first-child { grid-column: 1 / -1; }
        }
      `}</style>
    </footer>
  );
}